import React from 'react';
import { API } from '../api';

/*
 * Getting the brief right before paying for the picture.
 *
 * A poster costs between five and thirty-eight cents and the best part of a
 * minute. This costs a fraction of a cent. So the cheapest route to a poster
 * the club likes is to spend almost nothing working out what they meant,
 * rather than generating four and keeping the least wrong one.
 *
 * It opens by drafting a brief from what the app already knows — the
 * competition, the club's colours, the look its posters keep having — so a
 * starting brief needs no typing at all. Then it asks the one or two things
 * it genuinely cannot work out, and redrafts each time one is answered. The
 * loop continues for as long as it is useful and stops asking when the brief
 * is good enough to generate from.
 *
 * The brief always lands in the admin's own box, editable. This suggests; it
 * never takes the pen away.
 */

function BriefAssistant({
  clubId, competitionId, eventId, eventIds, brief, fields, settings, onApply, showToast,
}) {
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [questions, setQuestions] = React.useState([]);
  const [answers, setAnswers] = React.useState([]);
  const [typed, setTyped] = React.useState({});
  const [rounds, setRounds] = React.useState(0);
  /* What it suggested for the words, held until applied. A headline and a
     call to action are the club's own voice, so they are offered rather than
     written straight into the form. */
  const [words, setWords] = React.useState(null);

  const call = async nextAnswers => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/posters/brief-assist`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clubId, competitionId, eventId, eventIds, settings,
          brief, fields, answers: nextAnswers,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) { showToast(data.error || 'It could not help just now', 'red'); return; }
      if (data.brief) onApply({ description: data.brief });
      /* Only what is new. Suggesting the headline the club already typed, or
         the one already being printed, is noise dressed up as help. */
      const suggested = {};
      if (data.headline && data.headline !== (fields.headline || '')) suggested.headline = data.headline;
      if (data.callToAction && data.callToAction !== (fields.callToAction || '')) {
        suggested.callToAction = data.callToAction;
      }
      setWords(Object.keys(suggested).length ? suggested : null);
      setQuestions(data.questions || []);
      setAnswers(nextAnswers);
      setTyped({});
      setRounds(n => n + 1);
      setOpen(true);
    } finally { setBusy(false); }
  };

  const answer = (question, value) => {
    if (!value || !String(value).trim()) return;
    call([...answers, { question: question.text, answer: String(value).trim() }]);
  };

  return (
    <div className="brief-assist">
      <div className="brief-assist-bar">
        <button type="button" className="w3-button w3-small w3-border"
          onClick={() => call(answers)} disabled={busy}>
          {busy
            ? <><i className="fa fa-spinner fa-spin w3-margin-right"></i>Thinking…</>
            : <><i className="fa fa-magic w3-margin-right"></i>
              {rounds === 0
                ? (brief && brief.trim() ? 'Help me improve this' : 'Write me a starting brief')
                : 'Improve it again'}</>}
        </button>
        {rounds === 0 && (
          <span className="brief-assist-why">
            Costs a fraction of a cent, and saves generating the wrong poster twice.
          </span>
        )}
        {rounds > 0 && questions.length === 0 && !busy && (
          <span className="brief-assist-why">
            Nothing else worth asking — this is ready to generate.
          </span>
        )}
      </div>

      {open && words && (
        <div className="brief-words">
          <span className="gs-section-label">Suggested wording</span>
          {words.headline && (
            <div className="brief-word">
              <span className="brief-word-label">Headline</span>
              <span className="brief-word-text">{words.headline}</span>
              <button type="button" className="w3-button w3-small w3-border"
                onClick={() => { onApply({ headline: words.headline }); setWords(w => ({ ...w, headline: null })); }}>
                Use it
              </button>
            </div>
          )}
          {words.callToAction && (
            <div className="brief-word">
              <span className="brief-word-label">Call to action</span>
              <span className="brief-word-text">{words.callToAction}</span>
              <button type="button" className="w3-button w3-small w3-border"
                onClick={() => {
                  onApply({ callToAction: words.callToAction });
                  setWords(w => ({ ...w, callToAction: null }));
                }}>
                Use it
              </button>
            </div>
          )}
          <p className="brief-words-note">
            Printed exactly as written. The date, cost and club name are already on the poster
            from your own records — these are the lines only you can write.
          </p>
        </div>
      )}

      {open && questions.length > 0 && (
        <div className="brief-assist-panel">
          <span className="gs-section-label">
            {questions.length === 1 ? 'One thing it needs to know' : 'A couple of things it needs to know'}
          </span>
          {questions.map(q => (
            <div key={q.id} className="brief-question">
              <p>{q.text}</p>
              <div className="brief-options">
                {q.options.map(o => (
                  <button key={o} type="button" className="w3-button w3-small w3-border"
                    disabled={busy} onClick={() => answer(q, o)}>
                    {o}
                  </button>
                ))}
                <input className="w3-input brief-own" placeholder="or say it your own way"
                  aria-label={q.text}
                  value={typed[q.id] || ''}
                  onChange={e => setTyped(t => ({ ...t, [q.id]: e.target.value }))}
                  onKeyDown={e => { if (e.key === 'Enter') answer(q, typed[q.id]); }} />
              </div>
            </div>
          ))}
          {/* Never a dead end: the brief is already usable and the questions
              are an offer, not a form to complete. */}
          <button type="button" className="brief-skip" onClick={() => setQuestions([])}>
            Skip these — the brief is good enough
          </button>
        </div>
      )}
    </div>
  );
}

export default BriefAssistant;
